import { TONE_CLASSES, type Tone } from "./tokens";

export interface ModalHandle {
  readonly element: HTMLElement;
  setTitle(title: string): void;
  setBody(node: HTMLElement): void;
  open(): void;
  close(): void;
  isOpen(): boolean;
}

export interface ModalProps {
  readonly title: string;
  readonly tone?: Tone;
  readonly onClose?: () => void;
}

// 中央モーダル。root は hidden トグル(block)、内側 centerer が flex 中央寄せ
// (hidden と flex の display 競合を避ける)。フォーカストラップ + Esc + 背景クリックで閉じる。
export function createModal(props: ModalProps): ModalHandle {
  let open = false;
  let lastFocused: HTMLElement | null = null;
  const tone = props.tone ?? "neutral";

  const root = document.createElement("div");
  root.className = "fixed inset-0 z-50 hidden";
  root.setAttribute("aria-hidden", "true");

  const backdrop = document.createElement("div");
  backdrop.className = "absolute inset-0 bg-slate-900/40";

  const centerer = document.createElement("div");
  centerer.className = "relative flex min-h-full items-center justify-center p-4";

  const dialog = document.createElement("div");
  dialog.tabIndex = -1;
  dialog.className = "w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-label", props.title);

  const header = document.createElement("div");
  header.className =
    tone === "danger"
      ? "border-b px-5 py-3 " + TONE_CLASSES.danger.bar
      : "border-b border-slate-200 px-5 py-3 text-slate-900";
  const titleEl = document.createElement("h2");
  titleEl.className = "text-base font-medium";
  titleEl.textContent = props.title;
  header.append(titleEl);

  const body = document.createElement("div");
  body.className = "px-5 py-4";

  dialog.append(header, body);
  centerer.append(dialog);
  root.append(backdrop, centerer);

  function trap(e: KeyboardEvent): void {
    const f = dialog.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
    );
    if (f.length === 0) return;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") api.close();
    else if (e.key === "Tab") trap(e);
  }

  const api: ModalHandle = {
    element: root,
    setTitle(t) {
      titleEl.textContent = t;
      dialog.setAttribute("aria-label", t);
    },
    setBody(node) {
      body.replaceChildren(node);
    },
    open() {
      if (open) return;
      open = true;
      lastFocused = document.activeElement as HTMLElement | null;
      root.classList.remove("hidden");
      root.setAttribute("aria-hidden", "false");
      document.addEventListener("keydown", onKeydown);
      const focusable = dialog.querySelector<HTMLElement>("button,a[href],input,select,textarea");
      (focusable ?? dialog).focus();
    },
    close() {
      if (!open) return;
      open = false;
      root.classList.add("hidden");
      root.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", onKeydown);
      lastFocused?.focus();
      props.onClose?.();
    },
    isOpen() {
      return open;
    },
  };
  backdrop.addEventListener("click", () => api.close());
  return api;
}

export interface ConfirmModalProps {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: Tone;
  readonly onConfirm: () => void;
}

// 確認モーダル。body 直下に append して即 open。閉じたら DOM から除去する。
export function confirmModal(props: ConfirmModalProps): ModalHandle {
  let handle: ModalHandle;
  handle = createModal({
    title: props.title,
    ...(props.tone !== undefined ? { tone: props.tone } : {}),
    onClose: () => {
      handle.element.remove();
    },
  });

  const wrap = document.createElement("div");
  wrap.className = "space-y-4";
  const msg = document.createElement("p");
  msg.className = "text-sm text-slate-700";
  msg.textContent = props.message;

  const actions = document.createElement("div");
  actions.className = "flex justify-end gap-2";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  cancel.textContent = props.cancelLabel ?? "キャンセル";
  cancel.addEventListener("click", () => handle.close());
  const confirm = document.createElement("button");
  confirm.type = "button";
  const danger = (props.tone ?? "neutral") === "danger";
  confirm.className = danger
    ? "rounded bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800"
    : "rounded bg-shinonome-700 px-3 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
  confirm.textContent = props.confirmLabel ?? "確認";
  confirm.addEventListener("click", () => {
    props.onConfirm();
    handle.close();
  });
  actions.append(cancel, confirm);
  wrap.append(msg, actions);

  document.body.append(handle.element);
  handle.setBody(wrap);
  handle.open();
  return handle;
}
